import React from 'react';
import ProgressHeader from './ProgressHeader';
import ContinueButton from './common/ContinueButton';
import TestimonialCard from './common/TestimonialCard';
import { TESTIMONIALS, STATS, LEARNING_GOALS } from '../constants/onboardingData';
import imageOnboarding1 from '../../../assets/images/image-onboarding-1.webp';
import imageOnboarding2 from '../../../assets/images/image-onboarding-2.jpeg';

/**
 * Step 6: Social Proof et Finalisation
 */
export default function Step6SocialProof({
  formData,
  currentStepNumber,
  currentStepMessage,
  onComplete,
  onBack,
}) {
  // Récupérer les labels des objectifs sélectionnés
  const selectedGoalsLabels = formData.learningGoals.map(
    goalId => LEARNING_GOALS.find(g => g.id === goalId)
  ).filter(Boolean);

  return (
    <div className="min-h-[100dvh] bg-[#F0FDFA] flex flex-col">
      <ProgressHeader
        currentStep={currentStepNumber}
        message={currentStepMessage}
        showBack={true}
        onBack={onBack}
      />

        {/* Récap des objectifs */}
        <div className="bg-white rounded-2xl p-4 mb-4 border-2 shadow-lg border-gray-100">
          <p className="font-semibold text-gray-800 text-center mb-3">
            Vos objectifs pour les 40 prochains jours :
          </p>
          <div className="space-y-2">
            {selectedGoalsLabels.map((goal) => (
              <div key={goal.id} className="flex items-center gap-3 bg-[#F0FDFA] rounded-xl p-3">
                <div className="w-10 h-10 rounded-lg bg-[#2DD4BF] flex items-center justify-center text-xl">
                  {goal.icon}
                </div>
                <span className="font-medium text-gray-700">{goal.label}</span>
                <div className="ml-auto text-[#2DD4BF]">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bandeau GeniMi */}
        <div className="bg-gradient-to-br from-yellow-400 to-orange-400 rounded-2xl p-5 mb-6 text-center shadow-lg">
          <div className="w-12 h-12 rounded-full bg-transparent flex items-center justify-center mx-auto mb-3">
            <span className="text-4xl">🎯</span>
          </div>
          <h2 className="text-xl font-bold mb-2">
            GeniMi vous aide à atteindre ces objectifs
          </h2>
          <p className="text-xs">
            Un assistant IA qui accompagne votre enfant chaque jour, s'adapte à son niveau, et vous donne des résultats mesurables
          </p>
        </div>

        {/* Titre Social Proof */}
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">
          Des milliers de parents ont déjà réussi
        </h2>
        <div className="flex justify-center mb-6">
          <div className="inline-block bg-white border-2 border-yellow-400 rounded-2xl px-5 py-2 shadow-lg text-sm">
            <span className="text-yellow-500">⚡</span>
            <span className="text-gray-700 font-medium">{STATS.monthlySuccesses} nouvelles réussites ce mois-ci</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-2 shadow-lg border-2 border-emerald-200 text-center">
            <span className="text-3xl block mb-1">📚</span>
            <p className="text-2xl font-bold text-emerald-600">{STATS.lectureProgress}</p>
            <p className="text-xs text-gray-500 font-semibold">progressent en lecture</p>
            <p className="text-xs text-gray-400">En moins de 2 mois</p>
          </div>
          <div className="bg-white rounded-2xl p-2 shadow-lg border-2 border-emerald-200 text-center">
            <span className="text-3xl block mb-1">🔢</span>
            <p className="text-2xl font-bold text-emerald-600">{STATS.mathsProgress}</p>
            <p className="text-xs text-gray-500 font-semibold">progressent en maths</p>
            <p className="text-xs text-gray-400">Résultats mesurables</p>
          </div>
          <div className="bg-white rounded-2xl p-2 shadow-lg border-2 border-emerald-200 text-center">
            <span className="text-3xl block mb-1">👨‍👩‍👧</span>
            <p className="text-2xl font-bold text-emerald-600">{STATS.familiesCount}</p>
            <p className="text-xs text-gray-500 font-semibold">familles africaines</p>
            <p className="text-xs text-gray-400">Nous font confiance</p>
          </div>
        </div>

        <div className="flex-1 px-4 pb-4 overflow-y-auto">
        {/* Section Images avec KPIs */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {/* Image 1 */}
          <div className="relative rounded-2xl overflow-hidden border-2 border-[#2DD4BF]">
            <img 
              src={imageOnboarding1} 
              alt="Enfant qui progresse" 
              className="w-full h-40 object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
              <p className="text-white text-xs font-medium flex items-center gap-1">
                <span>✨</span> 2 347 enfants ont progressé ce mois-ci
              </p>
            </div>
          </div>
          
          {/* Image 2 */}
          <div className="relative rounded-2xl overflow-hidden border-2 border-[#2DD4BF]">
            <img 
              src={imageOnboarding2} 
              alt="Résultats qui changent des vies" 
              className="w-full h-40 object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
              <p className="text-white text-xs font-medium flex items-center gap-1">
                <span>✨</span> Des résultats qui changent des vies
              </p>
            </div>
          </div>
        </div>

        {/* Témoignages */}
        <div className="space-y-4 mb-6">
          {TESTIMONIALS.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>

        {/* Bandeau Garantie */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl p-5 shadow-2xl mb-6 text-white">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-2xl">🎯</span>
            <span className="font-bold text-lg">Garantie Résultats Visibles</span>
          </div>
          <p className="text-white/90 text-sm">
            92% des parents constatent des progrès mesurables en moins de 2 mois
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="sticky bottom-0 bg-[#F0FDFA] p-4 border-t border-gray-100">
        <ContinueButton
          onClick={onComplete}
          label="🚀 Obtenir ces résultats maintenant"
          variant="cta"
        />
        <div className="text-center mt-3 space-y-1">
          <p className="text-gray-600 text-sm flex items-center justify-center gap-1">
            <span>👥</span>
            <span className="text-emerald-600 font-bold">{STATS.weeklyParents} parents</span>
            ont rejoint cette semaine
          </p>
          <p className="text-gray-500 text-xs flex items-center justify-center gap-1">
            <span>⭐</span>
            Note moyenne : <span className="font-bold text-emerald-600">{STATS.averageRating}</span> sur {STATS.totalReviews} avis
          </p>
        </div>
      </div>
    </div>
  );
}
